import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { PlayerAuctionService } from '../player-auction.service';
import {
  ContractPublicOrderStatus,
  CorrectPublicSellContractCredentialsRequest,
  CONTRACT_CORRECTABLE_REASONS,
  CONTRACT_RESUMABLE_REASONS,
} from '../player-auction.type';
import { parseBackupCodes, pastedBackupCodes } from '../../shared/credentials';

interface ContractFailureGuide {
  message: string;
  guideTitle: string;
  steps: string[];
}

/** User-facing text + fix-it guide for contract failed_reason codes that are resumable or correctable. */
const CONTRACT_FAILURE_GUIDES: Record<string, ContractFailureGuide> = {
  wrong_password: {
    message: 'Incorrect password. Please update your credentials.',
    guideTitle: 'How to change your EA account password',
    steps: [
      'Go to account.ea.com in a browser and sign in with your EA account email and current password.',
      'Open "Sign In & Security" from the account menu.',
      'Select "Change Password", enter your current password, then enter and confirm the new password.',
      'Click "Save" / "Update Password" to confirm the change.',
      'Come back here and enter the new password in the "Fix Credentials" form so we can resume your transfer.',
    ],
  },
  wrong_backup_code: {
    message: 'Incorrect backup code. Please update your credentials.',
    guideTitle: 'How to get new EA account backup codes',
    steps: [
      'Go to account.ea.com in a browser and sign in.',
      'Open "Sign In & Security" from the account menu.',
      'Under "Login Verification", find the backup codes section.',
      'Select "Get new codes" — EA will invalidate the old codes and issue 6 new ones.',
      'Write down or screenshot all 6 new codes, in order.',
      'Come back here and enter the unused backup codes (comma-separated) in the "Fix Credentials" form — only one is required, but entering more helps avoid this happening again.',
    ],
  },
  signed_into_another_device: {
    message: 'The account is signed into another device. Log it out there, then click Resume.',
    guideTitle: 'How to log out of Ultimate Team on another device',
    steps: [
      'Launch the Game — Open EA Sports FC 26 on the console/PC that is currently signed in. Wait for the main splash screen to load.',
      'Enter Ultimate Team — On the main menu, select "Ultimate Team" (usually a large tile/icon, often green and gold themed). Press the confirm button (X/A/Enter depending on platform).',
      'Wait for UT to Load — The game will show a loading screen, then take you to the Ultimate Team Hub, where you\'ll see your squad, coins, and daily objectives.',
      'Exit Ultimate Team — From the UT Hub, look for the "Back" or "Exit" button (usually bottom-left corner or mapped to B/Circle/Esc). Press it once or twice until you\'re prompted to leave UT.',
      'Confirm Exit — A confirmation popup may appear asking "Are you sure you want to exit Ultimate Team?" Select Yes/Confirm.',
      'Return to Main Menu — After exiting, the game will load back to the Main Menu, where you\'ll see options like Career Mode, Ultimate Team, Kick Off, Clubs, etc.',
    ],
  },
  transfer_list_full: {
    message: 'The transfer list is full. Clear some space, then click Resume.',
    guideTitle: 'How to clear space in your Ultimate Team transfer list',
    steps: [
      'Open Ultimate Team and go to "Transfers" then "Transfer List".',
      'Sort your list (e.g. by duration or price) so it\'s easy to find items to act on.',
      'Sell or Quick Sell cards until at least 60 slots are free — the transfer list holds 100 cards total, so no more than 40 should remain listed.',
      'Double-check your listed count shows 40 or fewer cards.',
      'Come back here and click Resume to continue.',
    ],
  },
  not_enough_coin: {
    message: 'Not enough coins in the account. Add coins, then click Resume.',
    guideTitle: 'Coin balance required to start the transfer',
    steps: [
      'Open Ultimate Team and check the coin balance in the top bar.',
      'The account needs at least 20,000 coins available before we can start the transfer.',
      'Once the balance is at least 20K coins, come back here and click Resume to continue.',
    ],
  },
  two_factor_not_enabled: {
    message: 'Two-factor authentication is not enabled on the account. Enable it, then click Resume.',
    guideTitle: 'How to enable two-factor authentication (Login Verification) on your EA account',
    steps: [
      'Go to account.ea.com in a browser and sign in.',
      'Open "Sign In & Security" from the account menu.',
      'Find "Login Verification" and select "Get Started" / "Turn On".',
      'Choose a verification method (email or authenticator app) and follow the prompts to confirm it.',
      'Save the backup codes shown during setup — write down or screenshot all 6, in order.',
      'Once enabled, come back here and click Resume to continue.',
    ],
  },
};

@Component({
  selector: 'app-contract-status',
  imports: [
    CommonModule,
    FormsModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatChipsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  templateUrl: './contract-status.html',
  styleUrl: './contract-status.scss',
})
// Public, customer-facing status page for a sell contract. It used to be
// OrderStatusComponent with a 'bot' | 'contract' mode flag; the bot half went
// with the ORDER module, so everything here is now contract-only.
export class ContractStatusComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _playerAuctionService = inject(PlayerAuctionService);
  private _destroyRef = inject(DestroyRef);

  orderId = '';
  order: ContractPublicOrderStatus | null = null;
  loading = true;
  fetchError = false;

  startScreenshotUrl: string | null = null;
  endScreenshotUrl: string | null = null;
  startScreenshotFetched = false;
  endScreenshotFetched = false;

  resumeLoading = false;
  resumeError: string | null = null;
  showGuide = false;

  showCredsForm = false;
  credsPassword = '';
  credsBackupCodes = '';
  correctCredsLoading = false;
  correctCredsError: string | null = null;

  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _destroyed = false;

  ngOnInit(): void {
    this.orderId = this._route.snapshot.paramMap.get('orderId')!;
    this._destroyRef.onDestroy(() => {
      this._destroyed = true;
      this._clearTimer();
      this._revokeUrls();
    });
    this._poll();
  }

  private _poll(): void {
    const fetch$: Observable<ContractPublicOrderStatus> =
      this._playerAuctionService.getPublicContractReport(this.orderId);

    fetch$.subscribe({
      next: (status) => {
        this.order = status;
        this.loading = false;
        this.fetchError = false;
        this._fetchScreenshots(status);

        if (!this._destroyed) {
          if (this.isTerminal) return;
          const interval = this._pollInterval();
          this._pollTimer = setTimeout(() => this._poll(), interval);
        }
      },
      error: () => {
        this.loading = false;
        this.fetchError = true;
        if (!this._destroyed) {
          this._pollTimer = setTimeout(() => this._poll(), 10_000);
        }
      },
    });
  }

  // A sell contract has no resting state (that was the order loop's transfer
  // limit), so the interval is fixed.
  private _pollInterval(): number {
    return 5_000;
  }

  private _fetchScreenshots(status: ContractPublicOrderStatus): void {
    const startTrigger = status.status !== 'Pending';
    const endTrigger = status.status === 'Done';
    const getScreenshot$ = (type: 'start' | 'end'): Observable<Blob> =>
      this._playerAuctionService.getPublicSellContractScreenshot(this.orderId, type);

    if (!this.startScreenshotFetched && startTrigger) {
      this.startScreenshotFetched = true;
      getScreenshot$('start').subscribe({
        next: (blob) => {
          if (this.startScreenshotUrl) URL.revokeObjectURL(this.startScreenshotUrl);
          this.startScreenshotUrl = URL.createObjectURL(blob);
        },
        error: () => { },
      });
    }

    if (!this.endScreenshotFetched && endTrigger) {
      this.endScreenshotFetched = true;
      getScreenshot$('end').subscribe({
        next: (blob) => {
          if (this.endScreenshotUrl) URL.revokeObjectURL(this.endScreenshotUrl);
          this.endScreenshotUrl = URL.createObjectURL(blob);
        },
        error: () => { },
      });
    }
  }

  /** Resume/correct-credentials actions, shown only for resumable/correctable failures. */
  doResume(): void {
    if (this.resumeLoading) return;
    this.resumeLoading = true;
    this.resumeError = null;

    this._playerAuctionService.resumePublicSellContract(this.orderId).subscribe({
      next: () => {
        this.resumeLoading = false;
        this._refreshNow();
      },
      error: (err: any) => {
        this.resumeLoading = false;
        this.resumeError = err?.error?.error ?? 'Action failed. Please try again.';
      },
    });
  }

  onBackupCodesPaste(event: ClipboardEvent): void {
    const codes = pastedBackupCodes(event);
    this.credsBackupCodes = [...parseBackupCodes(this.credsBackupCodes), ...codes].join(', ');
  }

  doCorrectCreds(): void {
    if (this.correctCredsLoading) return;

    const request: CorrectPublicSellContractCredentialsRequest = {};
    if (this.credsPassword.trim()) request.password = this.credsPassword.trim();
    if (this.credsBackupCodes.trim()) {
      const codes = parseBackupCodes(this.credsBackupCodes).slice(0, 6);
      const backupFields: (keyof CorrectPublicSellContractCredentialsRequest)[] = [
        'backup_code_1', 'backup_code_2', 'backup_code_3', 'backup_code_4', 'backup_code_5', 'backup_code_6',
      ];
      codes.forEach((code, i) => { request[backupFields[i]] = code; });
    }
    if (!Object.keys(request).length) {
      this.correctCredsError = this.contractCredsField === 'backup_code'
        ? 'At least one backup code is required.'
        : 'Password is required.';
      return;
    }
    this.correctCredsLoading = true;
    this.correctCredsError = null;
    this._playerAuctionService.correctPublicSellContractCredentials(this.orderId, request).subscribe({
      next: () => {
        this.correctCredsLoading = false;
        this.showCredsForm = false;
        this._refreshNow();
      },
      error: (err) => {
        this.correctCredsLoading = false;
        this.correctCredsError = err?.error?.error ?? 'Failed to update credentials.';
      },
    });
  }

  /** Re-fetches status immediately (bypassing the poll timer) after a resume/correct-creds action. */
  private _refreshNow(): void {
    this._clearTimer();
    this._poll();
  }

  /** The report endpoint returns the raw (capitalized) contract status. */
  private get _contractOrder(): ContractPublicOrderStatus | null {
    return this.order;
  }

  /** Fix-it guide for the current contract failure reason, when one is resumable or correctable. */
  get failureGuide(): ContractFailureGuide | null {
    const contract = this._contractOrder;
    if (!contract || contract.status !== 'Failed') return null;
    return CONTRACT_FAILURE_GUIDES[contract.error_msg] ?? null;
  }

  /** Which single creds field is relevant to the failure reason (null = show all, e.g. outsourcing). */
  get contractCredsField(): 'password' | 'backup_code' | null {
    const contract = this._contractOrder;
    if (!contract) return null;
    if (contract.error_msg === 'wrong_password') return 'password';
    if (contract.error_msg === 'wrong_backup_code') return 'backup_code';
    return null;
  }

  get showResumeButton(): boolean {
    const contract = this._contractOrder;
    if (!contract) return false;
    return contract.status === 'Failed'
      && (CONTRACT_RESUMABLE_REASONS as readonly string[]).includes(contract.error_msg);
  }

  get showCredsSection(): boolean {
    const contract = this._contractOrder;
    if (!contract) return false;
    return contract.status === 'Failed'
      && (CONTRACT_CORRECTABLE_REASONS as readonly string[]).includes(contract.error_msg);
  }

  private _clearTimer(): void {
    if (this._pollTimer !== null) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private _revokeUrls(): void {
    if (this.startScreenshotUrl) URL.revokeObjectURL(this.startScreenshotUrl);
    if (this.endScreenshotUrl) URL.revokeObjectURL(this.endScreenshotUrl);
  }

  get statusLabel(): string {
    return this.order?.status ?? '';
  }

  get errorMessage(): string | null {
    if (!this.order) return null;
    return this.failureGuide?.message ?? null;
  }

  /** Logging in mid-transfer kicks the bot out, so warn until the order is Done (or paused on a failure). */
  get showLoginWarning(): boolean {
    return !!this.order && !this.isTerminal && this.order.status !== 'Failed';
  }

  get isTerminal(): boolean {
    if (!this.order) return false;
    return this.order.status === 'Done';
  }

  get progressPercentage(): number {
    const percent = (this.order?.transferred_coins || 0) / (this.order?.target_coins || 1) * 100;
    return Math.floor(percent);
  }
}
